import React, { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { htmlToMd, normalizeUrl } from '../lib/htmlToMd';

// Shared Markdown renderer for sticky-note content. Notes are stored as
// Markdown; htmlToMd() is a defensive passthrough that still converts any
// legacy HTML note (or one in transit through shared_todos) on the fly.
const InCodeBlock = createContext(false);

const MD_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>['components'] = {
  h1: ({ children }) => <h1 className="text-base font-bold mb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-semibold mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
  p:  ({ children }) => <p className="mb-1">{children}</p>,
  // A list may mix task items and plain items, so the bullet is suppressed
  // per task item rather than on the whole list. The checkbox sits in the
  // bullet's gutter so nested lists still stack below the item.
  ul: ({ children }) => <ul className="list-disc ml-4 mb-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-4 mb-1">{children}</ol>,
  li: ({ className, children }) => className?.includes('task-list-item')
    ? <li className="list-none relative -ml-4 pl-5">{children}</li>
    : <li>{children}</li>,
  input: ({ checked, type }) => type === 'checkbox'
    ? <input type="checkbox" checked={!!checked} readOnly className="absolute left-0 top-1 accent-indigo-600" />
    : null,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => <a href={normalizeUrl(href)} className="text-indigo-600 underline" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{children}</a>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-gray-300 pl-2 text-gray-500 italic">{children}</blockquote>,
  hr: () => <hr className="my-2 border-gray-200" />,
  pre: ({ children }) => (
    <InCodeBlock.Provider value={true}>
      <pre className="bg-gray-700 text-gray-100 rounded p-2 my-1 overflow-x-auto font-mono text-xs">{children}</pre>
    </InCodeBlock.Provider>
  ),
  code: ({ children }) => {
    const inCodeBlock = useContext(InCodeBlock);
    return inCodeBlock
      ? <code className="font-mono text-xs">{children}</code>
      : <code className="bg-gray-200 rounded px-1 font-mono text-xs">{children}</code>;
  },
  table: ({ children }) => (
    <div className="my-1 overflow-x-auto">
      <table className="border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-gray-300 px-1.5 py-0.5 bg-gray-100 font-semibold text-left">{children}</th>,
  td: ({ children }) => <td className="border border-gray-300 px-1.5 py-0.5 align-top">{children}</td>,
};

interface NotesMarkdownProps {
  notes: string | null | undefined;
  className?: string;
}

export function NotesMarkdown({ notes, className }: NotesMarkdownProps) {
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={MD_COMPONENTS}>
        {htmlToMd(notes || '')}
      </ReactMarkdown>
    </div>
  );
}
